import {
  SessionTimeline,
  ClockModel,
  ClockSegment,
  ClockStationSegment,
  ClockGapSegment,
  ClockLeBand,
  ClockAnswerMarker,
} from 'shared/types';

const parseMs = (s: string | null | undefined): number | null => {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
};

/**
 * Effective [start, end] in ms for a row, applying the same heartbeat fallback
 * the backend uses for bogus (epoch-zero / pre-entry) exits, then a duration
 * fallback, then clamping into the session window.
 */
const effInterval = (
  enteredAt: string | null,
  exitedAt: string | null,
  heartbeatAt: string | null,
  durationSeconds: number | null,
  startMs: number,
  endMs: number,
): [number, number] | null => {
  const entered = parseMs(enteredAt);
  if (entered == null) return null;

  let exit = parseMs(exitedAt);
  const hb = parseMs(heartbeatAt);
  if ((exit == null || exit < entered) && hb != null && hb >= entered) {
    exit = hb;
  }
  if (exit == null || exit < entered) {
    const d = durationSeconds && durationSeconds > 0 ? durationSeconds * 1000 : 0;
    exit = entered + d;
  }

  const clampedStart = Math.max(startMs, Math.min(entered, endMs));
  const clampedEnd = Math.max(startMs, Math.min(exit, endMs));
  return [clampedStart, Math.max(clampedStart, clampedEnd)];
};

const round = (n: number): number => Math.round(n * 10) / 10;

/**
 * Turns the raw timeline payload into the geometry both the clock and the linear
 * strip render. Pass `userId` to scope to a single attendee (null = all rows).
 *
 * Returns null when there's no usable session window (can't lay anything out).
 */
export const buildClockModel = (
  timeline: SessionTimeline,
  userId: string | null,
): ClockModel | null => {
  const matchesUser = (id: string | null): boolean => userId == null || id === userId;

  const stationsRaw = timeline.stations.filter(s => matchesUser(s.user_id));
  const lesRaw = timeline.learningExperiences.filter(l => matchesUser(l.user_id));
  const progress = timeline.progressReports.filter(p => matchesUser(p.user_id));
  const answersRaw = timeline.answers.filter(a => matchesUser(a.user_id));

  // Establish the session window. Prefer the (effective) session bounds; fall
  // back to the span of the actual rows when timestamps are missing/bogus.
  let startMs = parseMs(timeline.sessionStart);
  let endMs = parseMs(timeline.sessionEnd);

  const allEntered = [
    ...stationsRaw.map(s => parseMs(s.entered_at)),
    ...lesRaw.map(l => parseMs(l.entered_at)),
  ].filter((n): n is number => n != null);
  const allExited = [
    ...stationsRaw.map(s => parseMs(s.exited_at)),
    ...stationsRaw.map(s => parseMs(s.last_heartbeat_at)),
    ...lesRaw.map(l => parseMs(l.exited_at)),
    ...lesRaw.map(l => parseMs(l.last_heartbeat_at)),
  ].filter((n): n is number => n != null);

  if (startMs == null && allEntered.length) startMs = Math.min(...allEntered);
  if ((endMs == null || (startMs != null && endMs <= startMs)) && allExited.length) {
    const maxExit = Math.max(...allExited.filter(n => startMs == null || n >= startMs));
    if (Number.isFinite(maxExit)) endMs = maxExit;
  }
  if (startMs == null) return null;
  if (endMs == null || endMs <= startMs) {
    endMs = startMs + Math.max(1, timeline.durationSeconds) * 1000;
  }

  const totalMs = endMs - startMs;
  const totalSec = totalMs / 1000;
  const toFrac = (ms: number): number => Math.min(1, Math.max(0, (ms - startMs!) / totalMs));
  const toSec = (ms: number): number => (ms - startMs!) / 1000;

  // Build station segments (with absolute ms windows) in time order.
  type Built = { seg: ClockStationSegment; sMs: number; eMs: number; leId: string | null };
  const built: Built[] = [];
  for (const st of stationsRaw) {
    const interval = effInterval(
      st.entered_at,
      st.exited_at,
      st.last_heartbeat_at,
      st.duration_seconds,
      startMs,
      endMs,
    );
    if (!interval) continue;
    const [sMs, eMs] = interval;
    const durationSec = (eMs - sMs) / 1000;
    const leName =
      lesRaw.find(l => l.id === st.le_session_id)?.learning_experience_name ??
      (st.learning_experience_id ? `LE ${st.learning_experience_id}` : null);

    built.push({
      sMs,
      eMs,
      leId: st.learning_experience_id,
      seg: {
        id: st.id,
        kind: 'station',
        stationNumber: st.station_number,
        leSessionId: st.le_session_id,
        leName,
        startFrac: toFrac(sMs),
        endFrac: toFrac(eMs),
        startSec: round(toSec(sMs)),
        endSec: round(toSec(eMs)),
        durationSec: round(durationSec),
        activeSec: 0,
        idleSec: round(durationSec),
        hasProgress: false,
      },
    });
  }
  built.sort((a, b) => a.sMs - b.sMs);

  // Attribute each progress report's active time to the specific station VISIT it
  // completed: match on station number, then pin to the visit whose [entered,
  // exited] window contains last_completed_station_at. This disambiguates a
  // station revisited within one LE session (le_session_id alone can't), so the
  // same report isn't double-counted across both visits.
  const TOL_MS = 2000; // small slack: completion can land just past exited_at
  for (const p of progress) {
    const active = p.time_in_station_seconds;
    if (active == null || Number.isNaN(active)) continue;
    const lcsa = parseMs(p.last_completed_station_at);
    const candidates = built.filter(
      b =>
        b.seg.stationNumber === p.last_completed_station &&
        (p.learning_experience_id == null || b.leId === p.learning_experience_id),
    );
    if (!candidates.length) continue;

    let target: Built | undefined;
    if (lcsa != null) {
      const inWindow = (b: Built): boolean => lcsa >= b.sMs - TOL_MS && lcsa <= b.eMs + TOL_MS;
      target = candidates.find(b => !b.seg.hasProgress && inWindow(b)) ?? candidates.find(inWindow);
    }
    if (!target) target = candidates.find(b => !b.seg.hasProgress) ?? candidates[0];

    const activeSec = Math.min(target.seg.durationSec, Math.max(0, active));
    if (!target.seg.hasProgress || activeSec > target.seg.activeSec) {
      target.seg.activeSec = round(activeSec);
      target.seg.idleSec = round(Math.max(0, target.seg.durationSec - activeSec));
      target.seg.hasProgress = true;
    }
  }

  const stationSegments: ClockStationSegment[] = built.map(b => b.seg);

  // Fill the timeline with gap segments (lobby / between stations / trailing) so
  // the strip has full coverage and idle stretches are visible.
  const segments: ClockSegment[] = [];
  const MIN_GAP_SEC = 1; // ignore sub-second tiling gaps
  let cursorMs = startMs;
  const pushGap = (fromMs: number, toMs: number, isLobby: boolean): void => {
    const durationSec = (toMs - fromMs) / 1000;
    if (durationSec < MIN_GAP_SEC) return;
    const gap: ClockGapSegment = {
      id: `gap-${fromMs}`,
      kind: isLobby ? 'lobby' : 'gap',
      label: isLobby ? 'Lobby / loading' : 'Idle (between stations)',
      startFrac: toFrac(fromMs),
      endFrac: toFrac(toMs),
      startSec: round(toSec(fromMs)),
      endSec: round(toSec(toMs)),
      durationSec: round(durationSec),
    };
    segments.push(gap);
  };

  for (const seg of stationSegments) {
    const segStartMs = startMs + seg.startFrac * totalMs;
    if (segStartMs > cursorMs) {
      pushGap(cursorMs, segStartMs, cursorMs === startMs);
    }
    segments.push(seg);
    cursorMs = Math.max(cursorMs, startMs + seg.endFrac * totalMs);
  }
  if (endMs > cursorMs) {
    pushGap(cursorMs, endMs, cursorMs === startMs);
  }

  // LE bands.
  const leBands: ClockLeBand[] = [];
  for (const le of lesRaw) {
    const interval = effInterval(
      le.entered_at,
      le.exited_at,
      le.last_heartbeat_at,
      le.duration_seconds,
      startMs,
      endMs,
    );
    if (!interval) continue;
    const [sMs, eMs] = interval;
    leBands.push({
      id: le.id,
      leName: le.learning_experience_name ?? (le.learning_experience_id ? `LE ${le.learning_experience_id}` : null),
      startFrac: toFrac(sMs),
      endFrac: toFrac(eMs),
      startSec: round(toSec(sMs)),
      endSec: round(toSec(eMs)),
      durationSec: round((eMs - sMs) / 1000),
    });
  }
  leBands.sort((a, b) => a.startFrac - b.startFrac);

  // Answer markers.
  const answers: ClockAnswerMarker[] = [];
  for (const a of answersRaw) {
    const at = parseMs(a.answered_at);
    if (at == null) continue;
    answers.push({
      id: a.id,
      frac: toFrac(at),
      sec: round(toSec(at)),
      isCorrect: a.is_correct,
      stationNumber: a.station_number,
    });
  }
  answers.sort((x, y) => x.frac - y.frac);

  // Aggregate stats: active + lobby + idle == total.
  const activeSec = stationSegments.reduce((sum, s) => sum + s.activeSec, 0);
  const lobbySec = segments
    .filter((s): s is ClockGapSegment => s.kind === 'lobby')
    .reduce((sum, s) => sum + s.durationSec, 0);
  const idleSec = Math.max(0, totalSec - activeSec - lobbySec);

  return {
    userId,
    totalSec: round(totalSec),
    startMs,
    endMs,
    segments,
    leBands,
    answers,
    activeSec: round(activeSec),
    idleSec: round(idleSec),
    lobbySec: round(lobbySec),
    stationCount: stationSegments.length,
    answerCount: answers.length,
    correctCount: answers.filter(a => a.isCorrect === true).length,
  };
};
